import {
  AlertCircle,
  ArrowRight,
  ImagePlus,
  LoaderCircle,
  LockKeyhole,
  Pencil,
  RotateCcw,
  ShieldAlert,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import QRCode from 'qrcode';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { minuteLabel } from '../api/client';
import { type Lot, useCreateLot, useLots, useUpdateLot, useUploadLotPhoto } from '../api/hooks';
import { Badge, Button, Card, ErrorNote, Field, Input, Loading, PageHeader, Panel } from '../components/ui';
import { useApiError } from '../i18n/useApiError';

interface LotForm {
  name: string;
  nameZh: string;
  addressLine: string;
  city: string;
  state: string;
  postalCode: string;
  latitude: string;
  longitude: string;
  timezone: string;
  capacity: string;
  spaceNumberingEnabled: boolean;
  spaceNumberStart: string;
  heightLimitCm: string;
  evChargers: string;
  accessNoteEn: string;
  accessNoteZh: string;
  photos: string[];
  status: 'draft' | 'online' | 'offline';
  availabilityStatus: 'unknown' | 'available' | 'busy' | 'full';
}

const emptyForm = (): LotForm => ({
  name: '',
  nameZh: '',
  addressLine: '',
  city: 'Chicago',
  state: 'IL',
  postalCode: '',
  latitude: '41.8781',
  longitude: '-87.6298',
  timezone: 'America/Chicago',
  capacity: '100',
  spaceNumberingEnabled: true,
  spaceNumberStart: '',
  heightLimitCm: '',
  evChargers: '0',
  accessNoteEn: '',
  accessNoteZh: '',
  photos: [],
  status: 'draft',
  availabilityStatus: 'unknown',
});

function formFromLot(lot: Lot): LotForm {
  return {
    name: lot.name,
    nameZh: lot.nameZh ?? '',
    addressLine: lot.addressLine,
    city: lot.city,
    state: lot.state,
    postalCode: lot.postalCode ?? '',
    latitude: String(lot.latitude),
    longitude: String(lot.longitude),
    timezone: lot.timezone,
    capacity: String(lot.capacity),
    spaceNumberingEnabled: lot.spaceNumberingEnabled !== false,
    spaceNumberStart: String(lot.spaceNumberStart),
    heightLimitCm: lot.heightLimitCm === null ? '' : String(lot.heightLimitCm),
    evChargers: String(lot.evChargers),
    accessNoteEn: lot.accessNoteEn ?? '',
    accessNoteZh: lot.accessNoteZh ?? '',
    photos: [...lot.photos],
    status: lot.status,
    availabilityStatus: lot.availabilityStatus,
  };
}

export function LotSetupPage({ lotId, canEdit }: { lotId: string; canEdit: boolean }) {
  const { t } = useTranslation();
  const describeError = useApiError();
  const lots = useLots();
  const update = useUpdateLot();
  const create = useCreateLot();
  const lot = lots.data?.lots.find((item) => item.id === lotId);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<LotForm>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [photosUploading, setPhotosUploading] = useState(false);
  const [numberingDraft, setNumberingDraft] = useState({
    capacity: '',
    spaceNumberingEnabled: true,
    spaceNumberStart: '',
  });
  const [numberingEditing, setNumberingEditing] = useState(false);
  const [numberingConfirming, setNumberingConfirming] = useState(false);
  const [numberingError, setNumberingError] = useState<string | null>(null);
  const [numberingSaved, setNumberingSaved] = useState(false);
  const numberingDialogTitleId = useId();

  useEffect(() => {
    if (lot && !creating) {
      setForm(formFromLot(lot));
      setNumberingDraft({
        capacity: String(lot.capacity),
        spaceNumberingEnabled: lot.spaceNumberingEnabled !== false,
        spaceNumberStart: String(lot.spaceNumberStart),
      });
      setNumberingEditing(false);
      setNumberingConfirming(false);
    }
  }, [creating, lot]);

  if (lots.isPending) return <Loading />;
  if (!lot && !creating) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader
          title={t('lotSetup.title')}
          hint={t('lotSetup.hint')}
          actions={
            canEdit ? <Button onClick={() => setCreating(true)}>{t('lotSetup.newLot')}</Button> : undefined
          }
        />
        <Panel>{t(canEdit ? 'lotSetup.noLotsOwner' : 'lotSetup.noLotsStaff')}</Panel>
      </div>
    );
  }

  const set = <K extends keyof LotForm>(key: K, value: LotForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const numbering = creating ? form : numberingDraft;
  const capacity = Number(numbering.capacity);
  const spaceNumberStart = Number(numbering.spaceNumberStart);
  const spaceNumberEnd = spaceNumberStart + capacity - 1;
  const capacityValid = Number.isInteger(capacity) && capacity > 0;
  const spaceRangeValid =
    capacityValid &&
    (!numbering.spaceNumberingEnabled ||
      (/^\d{4}$/.test(numbering.spaceNumberStart) && spaceNumberStart >= 1000 && spaceNumberEnd <= 9999));
  const numberingChanged = Boolean(
    lot &&
      (capacity !== lot.capacity ||
        numbering.spaceNumberingEnabled !== (lot.spaceNumberingEnabled !== false) ||
        (numbering.spaceNumberingEnabled && spaceNumberStart !== lot.spaceNumberStart)),
  );

  const setNumbering = (key: 'capacity' | 'spaceNumberStart', value: string) => {
    if (creating) {
      set(key, value);
    } else {
      setNumberingDraft((current) => ({ ...current, [key]: value }));
    }
    setNumberingConfirming(false);
    setNumberingError(null);
    setNumberingSaved(false);
  };

  const setSpaceNumberingEnabled = (enabled: boolean) => {
    if (creating) {
      set('spaceNumberingEnabled', enabled);
    } else {
      setNumberingDraft((current) => ({ ...current, spaceNumberingEnabled: enabled }));
    }
    setNumberingConfirming(false);
    setNumberingError(null);
    setNumberingSaved(false);
  };

  const submit = async () => {
    setError(null);
    setSaved(false);
    if (creating && !spaceRangeValid) {
      setError(t('lotSetup.invalidSpaceRange'));
      return;
    }
    const body = {
      name: form.name,
      nameZh: form.nameZh || null,
      accessNoteEn: form.accessNoteEn || null,
      accessNoteZh: form.accessNoteZh || null,
      addressLine: form.addressLine,
      city: form.city,
      state: form.state.toUpperCase(),
      postalCode: form.postalCode,
      latitude: Number(form.latitude),
      longitude: Number(form.longitude),
      timezone: form.timezone,
      heightLimitCm: form.heightLimitCm ? Number(form.heightLimitCm) : null,
      evChargers: Number(form.evChargers),
      photos: form.photos,
      status: form.status,
      availabilityStatus: form.availabilityStatus,
    };
    try {
      if (creating) {
        const { status: _status, availabilityStatus: _availability, ...createBody } = body;
        await create.mutateAsync({
          ...createBody,
          capacity,
          spaceNumberingEnabled: form.spaceNumberingEnabled,
          ...(form.spaceNumberingEnabled ? { spaceNumberStart } : {}),
          nameZh: form.nameZh || undefined,
          accessNoteEn: form.accessNoteEn || undefined,
          accessNoteZh: form.accessNoteZh || undefined,
          heightLimitCm: form.heightLimitCm ? Number(form.heightLimitCm) : undefined,
        });
        setCreating(false);
      } else if (lot) {
        await update.mutateAsync({ id: lot.id, ...body });
      }
      setSaved(true);
    } catch (cause) {
      setError(describeError(cause, 'lotSetup.saveFailed'));
    }
  };

  const saveNumbering = async () => {
    if (!lot || !spaceRangeValid || !numberingChanged) return;
    setNumberingError(null);
    setNumberingSaved(false);
    try {
      await update.mutateAsync({
        id: lot.id,
        capacity,
        spaceNumberingEnabled: numbering.spaceNumberingEnabled,
        ...(numbering.spaceNumberingEnabled ? { spaceNumberStart } : {}),
      });
      setForm((current) => ({
        ...current,
        capacity: String(capacity),
        spaceNumberingEnabled: numbering.spaceNumberingEnabled,
        spaceNumberStart: String(spaceNumberStart),
      }));
      setNumberingConfirming(false);
      setNumberingEditing(false);
      setNumberingSaved(true);
    } catch (cause) {
      setNumberingConfirming(false);
      setNumberingError(describeError(cause, 'lotSetup.numberingSaveFailed'));
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={creating ? t('lotSetup.createTitle') : t('lotSetup.title')}
        hint={t('lotSetup.hint')}
        actions={
          <>
            {!creating && lot ? <StatusBadge status={lot.status} /> : null}
            {canEdit ? (
              <Button
                onClick={() => {
                  setCreating((value) => !value);
                  setForm(creating && lot ? formFromLot(lot) : emptyForm());
                  setSaved(false);
                  setNumberingEditing(false);
                  setNumberingConfirming(false);
                  setNumberingError(null);
                  setNumberingSaved(false);
                }}
              >
                {creating ? t('lotSetup.cancelCreate') : t('lotSetup.newLot')}
              </Button>
            ) : null}
            {canEdit ? (
              <Button
                variant="primary"
                disabled={
                  update.isPending || create.isPending || photosUploading || (creating && !spaceRangeValid)
                }
                onClick={() => void submit()}
              >
                {creating ? t('lotSetup.create') : t('lotSetup.save')}
              </Button>
            ) : null}
          </>
        }
      />

      {error ? <ErrorNote message={error} /> : null}
      {saved ? <Panel className="text-success">{t('lotSetup.saved')}</Panel> : null}

      <div className="grid gap-5 lg:grid-cols-[1fr_1fr_280px]">
        <div className="grid content-start gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
          <EditField
            label={t('lotSetup.name')}
            value={form.name}
            disabled={!canEdit}
            onChange={(value) => set('name', value)}
          />
          <EditField
            label={t('lotSetup.nameZh')}
            value={form.nameZh}
            disabled={!canEdit}
            onChange={(value) => set('nameZh', value)}
          />
          <EditField
            label={t('lotSetup.address')}
            value={form.addressLine}
            disabled={!canEdit}
            onChange={(value) => set('addressLine', value)}
          />
          <EditField
            label={t('lotSetup.city')}
            value={form.city}
            disabled={!canEdit}
            onChange={(value) => set('city', value)}
          />
          <EditField
            label={t('lotSetup.state')}
            value={form.state}
            disabled={!canEdit}
            onChange={(value) => set('state', value.toUpperCase().slice(0, 2))}
          />
          <EditField
            label={t('lotSetup.postalCode')}
            value={form.postalCode}
            disabled={!canEdit}
            onChange={(value) => set('postalCode', value)}
          />
          <EditField
            label={t('lotSetup.latitude')}
            value={form.latitude}
            type="number"
            disabled={!canEdit}
            onChange={(value) => set('latitude', value)}
          />
          <EditField
            label={t('lotSetup.longitude')}
            value={form.longitude}
            type="number"
            disabled={!canEdit}
            onChange={(value) => set('longitude', value)}
          />
          <EditField
            label={t('lotSetup.heightCm')}
            value={form.heightLimitCm}
            type="number"
            disabled={!canEdit}
            onChange={(value) => set('heightLimitCm', value)}
          />
          <EditField
            label={t('lotSetup.evChargers')}
            value={form.evChargers}
            type="number"
            disabled={!canEdit}
            onChange={(value) => set('evChargers', value)}
          />
          <EditField
            label={t('lotSetup.timezone')}
            value={form.timezone}
            disabled={!canEdit}
            onChange={(value) => set('timezone', value)}
          />
        </div>

        <div className="flex flex-col gap-3">
          <Field label={t('lotSetup.status')}>
            <SegmentedChoice
              value={form.status}
              disabled={!canEdit || creating}
              onChange={(status) => set('status', status)}
              className="grid-cols-3"
              options={(['draft', 'online', 'offline'] as const).map((status) => ({
                value: status,
                label: t(`lotSetup.${status}`),
              }))}
            />
          </Field>
          <Field
            label={t('lotSetup.availability')}
            hint={
              lot?.availabilityUpdatedAt
                ? t('lotSetup.availabilityUpdated', {
                    time: new Date(lot.availabilityUpdatedAt).toLocaleString(),
                  })
                : t('lotSetup.availabilityUnknownTime')
            }
          >
            <SegmentedChoice
              value={form.availabilityStatus}
              disabled={!canEdit || creating}
              onChange={(status) => set('availabilityStatus', status)}
              className="grid-cols-2 sm:grid-cols-4"
              options={(['unknown', 'available', 'busy', 'full'] as const).map((status) => ({
                value: status,
                label: t(`lotSetup.availabilityStatus.${status}`),
              }))}
            />
          </Field>
          <TextArea
            label={t('lotSetup.accessEn')}
            value={form.accessNoteEn}
            disabled={!canEdit}
            onChange={(value) => set('accessNoteEn', value)}
          />
          <TextArea
            label={t('lotSetup.accessZh')}
            value={form.accessNoteZh}
            disabled={!canEdit}
            onChange={(value) => set('accessNoteZh', value)}
          />
          <PhotoUploader
            key={creating ? 'new-lot' : lot?.id}
            photos={form.photos}
            disabled={!canEdit}
            onChange={(photos) => set('photos', photos)}
            onAdd={(url) => setForm((current) => ({ ...current, photos: [...current.photos, url] }))}
            onUploadingChange={setPhotosUploading}
          />

          {!creating && lot ? (
            <>
              <span className="text-[11px] text-muted">{t('lotSetup.hours')}</span>
              <div className="overflow-hidden rounded-lg border border-hairline">
                {Object.entries(lot.businessHours).map(([day, windows]) => (
                  <div
                    key={day}
                    className="flex justify-between border-b border-hairline px-3 py-2 text-[12px] last:border-b-0"
                  >
                    <span>{t(`lotSetup.weekday.${day}`, { defaultValue: day })}</span>
                    <span className="num text-muted">
                      {windows
                        .map(
                          (window) =>
                            `${minuteLabel(window.openMinute)}–${minuteLabel(window.closeMinute % 1440)}`,
                        )
                        .join(', ')}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>

        {!creating && lot ? <QrCard lot={lot} /> : <Panel>{t('lotSetup.qrAfterCreate')}</Panel>}
      </div>

      <Card className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-surface-muted text-ink">
              <LockKeyhole size={17} />
            </span>
            <div className="flex min-w-0 flex-col gap-1">
              <h2 className="text-sm font-bold">{t('lotSetup.numberingTitle')}</h2>
              <p className="max-w-2xl text-[11px] leading-relaxed text-muted">
                {t('lotSetup.numberingDescription')}
              </p>
            </div>
          </div>
          {!creating && lot && canEdit && !numberingEditing ? (
            <Button
              onClick={() => {
                setNumberingDraft({
                  capacity: String(lot.capacity),
                  spaceNumberingEnabled: lot.spaceNumberingEnabled !== false,
                  spaceNumberStart: String(lot.spaceNumberStart),
                });
                setNumberingEditing(true);
                setNumberingError(null);
                setNumberingSaved(false);
              }}
            >
              <Pencil size={14} /> {t('lotSetup.numberingEdit')}
            </Button>
          ) : null}
        </div>

        {numberingError ? <ErrorNote message={numberingError} /> : null}
        {numberingSaved ? <Panel className="text-success">{t('lotSetup.numberingSaved')}</Panel> : null}

        {creating || numberingEditing ? (
          <div className="flex flex-col gap-3">
            <SpaceNumberingToggle
              checked={numbering.spaceNumberingEnabled}
              disabled={!canEdit}
              label={t('lotSetup.numberingEnabled')}
              description={t('lotSetup.numberingEnabledDescription')}
              onChange={setSpaceNumberingEnabled}
            />
            <div className={`grid gap-3 ${numbering.spaceNumberingEnabled ? 'sm:grid-cols-2' : ''}`}>
              <EditField
                label={t(numbering.spaceNumberingEnabled ? 'lotSetup.capacity' : 'lotSetup.maximumCapacity')}
                value={numbering.capacity}
                type="number"
                disabled={!canEdit}
                onChange={(value) => setNumbering('capacity', value)}
              />
              {numbering.spaceNumberingEnabled ? (
                <EditField
                  label={t('lotSetup.spaceNumberStart')}
                  value={numbering.spaceNumberStart}
                  type="number"
                  disabled={!canEdit}
                  onChange={(value) => setNumbering('spaceNumberStart', value.slice(0, 4))}
                />
              ) : null}
            </div>
            <Panel className={spaceRangeValid ? 'text-success' : 'text-danger'}>
              {!numbering.spaceNumberingEnabled
                ? capacityValid
                  ? t('lotSetup.capacityLimitActive', { count: capacity })
                  : t('lotSetup.capacityLimitHint')
                : spaceRangeValid
                  ? t('lotSetup.spaceNumberRange', { start: spaceNumberStart, end: spaceNumberEnd })
                  : t('lotSetup.spaceNumberHint')}
            </Panel>
            {!creating && canEdit ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="dark"
                  disabled={!spaceRangeValid || !numberingChanged || update.isPending}
                  onClick={() => setNumberingConfirming(true)}
                >
                  {t('lotSetup.numberingReview')}
                </Button>
                <Button
                  onClick={() => {
                    if (lot) {
                      setNumberingDraft({
                        capacity: String(lot.capacity),
                        spaceNumberingEnabled: lot.spaceNumberingEnabled !== false,
                        spaceNumberStart: String(lot.spaceNumberStart),
                      });
                    }
                    setNumberingEditing(false);
                    setNumberingConfirming(false);
                    setNumberingError(null);
                  }}
                >
                  {t('lotSetup.numberingCancel')}
                </Button>
              </div>
            ) : null}
          </div>
        ) : lot ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Panel>
              <span className="block text-[10px] uppercase text-faint">
                {t('lotSetup.numberingCurrentRange')}
              </span>
              <strong className="num mt-1 block text-base text-ink">
                {lot.spaceNumberingEnabled !== false
                  ? `${lot.spaceNumberStart}–${lot.spaceNumberStart + lot.capacity - 1}`
                  : t('lotSetup.numberingDisabled')}
              </strong>
            </Panel>
            <Panel>
              <span className="block text-[10px] uppercase text-faint">
                {t(lot.spaceNumberingEnabled !== false ? 'lotSetup.capacity' : 'lotSetup.maximumCapacity')}
              </span>
              <strong className="num mt-1 block text-base text-ink">{lot.capacity}</strong>
            </Panel>
          </div>
        ) : null}

        {!creating ? (
          <div className="flex items-start gap-2 border-t border-hairline pt-3 text-[11px] text-muted">
            <ShieldAlert className="mt-0.5 shrink-0" size={14} />
            <span>{t('lotSetup.numberingProtected')}</span>
          </div>
        ) : null}
      </Card>

      {numberingConfirming && lot ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/45 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={numberingDialogTitleId}
            className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-col gap-1">
                <h2 id={numberingDialogTitleId} className="text-base font-bold">
                  {t('lotSetup.numberingConfirmTitle')}
                </h2>
                <p className="text-xs leading-relaxed text-muted">
                  {t('lotSetup.numberingConfirmDescription')}
                </p>
              </div>
              <button
                type="button"
                title={t('lotSetup.numberingCancel')}
                aria-label={t('lotSetup.numberingCancel')}
                onClick={() => setNumberingConfirming(false)}
                className="grid h-8 w-8 shrink-0 place-items-center rounded-lg hover:bg-surface-muted"
              >
                <X size={16} />
              </button>
            </div>

            <div className="my-5 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
              <Panel>
                <span className="block text-[10px] text-faint">{t('lotSetup.numberingCurrent')}</span>
                <strong className="num mt-1 block text-sm text-ink">
                  {lot.spaceNumberingEnabled !== false
                    ? `${lot.spaceNumberStart}–${lot.spaceNumberStart + lot.capacity - 1}`
                    : t('lotSetup.numberingDisabled')}
                </strong>
                <span className="num text-[10px] text-muted">
                  {t('lotSetup.numberingSpaceCount', { count: lot.capacity })}
                </span>
              </Panel>
              <ArrowRight className="text-faint" size={18} />
              <Panel className="bg-warning/10">
                <span className="block text-[10px] text-faint">{t('lotSetup.numberingNew')}</span>
                <strong className="num mt-1 block text-sm text-ink">
                  {numbering.spaceNumberingEnabled
                    ? `${spaceNumberStart}–${spaceNumberEnd}`
                    : t('lotSetup.numberingDisabled')}
                </strong>
                <span className="num text-[10px] text-muted">
                  {t('lotSetup.numberingSpaceCount', { count: capacity })}
                </span>
              </Panel>
            </div>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button onClick={() => setNumberingConfirming(false)}>{t('lotSetup.numberingCancel')}</Button>
              <Button variant="dark" disabled={update.isPending} onClick={() => void saveNumbering()}>
                {t('lotSetup.numberingConfirmAction')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SpaceNumberingToggle({
  checked,
  disabled,
  label,
  description,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  label: string;
  description: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 rounded-lg border border-hairline px-3 py-3 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent has-[:focus-visible]:ring-offset-1 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50">
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-xs font-semibold text-ink">{label}</span>
        <span className="text-[11px] leading-relaxed text-muted">{description}</span>
      </span>
      <span className="relative h-6 w-11 shrink-0">
        <input
          type="checkbox"
          role="switch"
          aria-checked={checked}
          className="peer sr-only"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span className="absolute inset-0 rounded-full bg-hairline transition peer-checked:bg-ink" />
        <span className="absolute left-1 top-1 h-4 w-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
      </span>
    </label>
  );
}

function SegmentedChoice<T extends string>({
  value,
  options,
  onChange,
  disabled,
  className,
  'aria-labelledby': labelledBy,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
  disabled?: boolean;
  className: string;
  'aria-labelledby'?: string;
}) {
  return (
    <fieldset
      aria-labelledby={labelledBy}
      className={`grid gap-1 rounded-lg border border-hairline bg-surface-muted p-1 ${className}`}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={`min-h-11 min-w-0 rounded-md px-2 text-[13px] font-semibold outline-none transition focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-45 ${
              selected
                ? 'bg-ink text-white shadow-sm'
                : 'bg-transparent text-muted hover:bg-white hover:text-ink'
            }`}
          >
            <span className="block break-words leading-tight">{option.label}</span>
          </button>
        );
      })}
    </fieldset>
  );
}

const MAX_PHOTOS = 8;
const MAX_PHOTO_BYTES = 8 * 1024 * 1024;
const ACCEPTED_PHOTO_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

interface PendingPhoto {
  id: string;
  file: File;
  previewUrl: string;
  status: 'uploading' | 'error';
  message?: string;
}

function PhotoUploader({
  photos,
  onChange,
  onAdd,
  onUploadingChange,
  disabled,
}: {
  photos: string[];
  onChange: (photos: string[]) => void;
  onAdd: (url: string) => void;
  onUploadingChange: (uploading: boolean) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const describeError = useApiError();
  const inputId = useId();
  const upload = useUploadLotPhoto();
  const mounted = useRef(true);
  const previewUrls = useRef(new Set<string>());
  const [pending, setPending] = useState<PendingPhoto[]>([]);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const uploading = pending.some((photo) => photo.status === 'uploading');

  useEffect(() => {
    onUploadingChange(uploading);
  }, [onUploadingChange, uploading]);

  useEffect(
    () => () => {
      mounted.current = false;
      for (const url of previewUrls.current) URL.revokeObjectURL(url);
      onUploadingChange(false);
    },
    [onUploadingChange],
  );

  const finishPreview = (url: string) => {
    URL.revokeObjectURL(url);
    previewUrls.current.delete(url);
  };

  const uploadOne = async (photo: PendingPhoto) => {
    setPending((current) =>
      current.map((item) =>
        item.id === photo.id ? { ...item, status: 'uploading', message: undefined } : item,
      ),
    );
    try {
      const result = await upload.mutateAsync(photo.file);
      if (!mounted.current) return;
      onAdd(result.url);
      setPending((current) => current.filter((item) => item.id !== photo.id));
      finishPreview(photo.previewUrl);
    } catch (cause) {
      if (!mounted.current) return;
      setPending((current) =>
        current.map((item) =>
          item.id === photo.id
            ? { ...item, status: 'error', message: describeError(cause, 'lotSetup.photoUploadFailed') }
            : item,
        ),
      );
    }
  };

  const selectFiles = (files: FileList | null) => {
    setSelectionError(null);
    const available = MAX_PHOTOS - photos.length - pending.length;
    if (!files || files.length === 0 || available <= 0) {
      if (available <= 0) setSelectionError(t('lotSetup.photoLimit', { count: MAX_PHOTOS }));
      return;
    }

    const accepted: File[] = [];
    for (const file of Array.from(files)) {
      if (!ACCEPTED_PHOTO_TYPES.has(file.type)) {
        setSelectionError(t('lotSetup.photoTypeError'));
      } else if (file.size > MAX_PHOTO_BYTES) {
        setSelectionError(t('lotSetup.photoSizeError'));
      } else if (accepted.length < available) {
        accepted.push(file);
      }
    }
    if (files.length > available) setSelectionError(t('lotSetup.photoLimit', { count: MAX_PHOTOS }));

    for (const file of accepted) {
      const previewUrl = URL.createObjectURL(file);
      previewUrls.current.add(previewUrl);
      const photo: PendingPhoto = {
        id: crypto.randomUUID(),
        file,
        previewUrl,
        status: 'uploading',
      };
      setPending((current) => [...current, photo]);
      void uploadOne(photo);
    }
  };

  const removePending = (photo: PendingPhoto) => {
    setPending((current) => current.filter((item) => item.id !== photo.id));
    finishPreview(photo.previewUrl);
  };

  const makePrimary = (index: number) => {
    const selected = photos[index];
    if (!selected) return;
    onChange([selected, ...photos.filter((_, photoIndex) => photoIndex !== index)]);
  };

  return (
    <Field
      label={t('lotSetup.photos')}
      hint={t('lotSetup.photoCount', { count: photos.length + pending.length, max: MAX_PHOTOS })}
    >
      <div className="flex flex-col gap-2">
        {photos.length > 0 || pending.length > 0 ? (
          <div className="grid grid-cols-2 gap-2 xl:grid-cols-3">
            {photos.map((url, index) => (
              <div
                key={url}
                className="group relative aspect-[4/3] overflow-hidden rounded-lg border border-hairline bg-surface-muted"
              >
                <img
                  src={url}
                  alt={t('lotSetup.photoAlt', { number: index + 1 })}
                  className="h-full w-full object-cover"
                />
                {index === 0 ? (
                  <span className="absolute left-1.5 top-1.5 rounded bg-ink px-1.5 py-1 text-[10px] font-semibold text-white">
                    {t('lotSetup.primaryPhoto')}
                  </span>
                ) : (
                  <button
                    type="button"
                    title={t('lotSetup.makePrimary')}
                    aria-label={t('lotSetup.makePrimary')}
                    onClick={() => makePrimary(index)}
                    disabled={disabled}
                    className="absolute left-1.5 top-1.5 grid h-8 w-8 place-items-center rounded-md bg-white/95 text-ink shadow-sm disabled:hidden"
                  >
                    <Star size={16} />
                  </button>
                )}
                <button
                  type="button"
                  title={t('lotSetup.removePhoto')}
                  aria-label={t('lotSetup.removePhoto')}
                  onClick={() => onChange(photos.filter((photo) => photo !== url))}
                  disabled={disabled}
                  className="absolute right-1.5 top-1.5 grid h-8 w-8 place-items-center rounded-md bg-white/95 text-danger shadow-sm disabled:hidden"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}

            {pending.map((photo) => (
              <div
                key={photo.id}
                className="relative aspect-[4/3] overflow-hidden rounded-lg border border-hairline bg-surface-muted"
              >
                <img
                  src={photo.previewUrl}
                  alt={photo.file.name}
                  className="h-full w-full object-cover opacity-55"
                />
                <div className="absolute inset-0 grid place-items-center bg-white/30">
                  {photo.status === 'uploading' ? (
                    <LoaderCircle
                      className="animate-spin text-accent"
                      size={24}
                      aria-label={t('lotSetup.uploading')}
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        title={t('lotSetup.retryUpload')}
                        aria-label={t('lotSetup.retryUpload')}
                        onClick={() => void uploadOne(photo)}
                        className="grid h-9 w-9 place-items-center rounded-md bg-white text-accent shadow-sm"
                      >
                        <RotateCcw size={17} />
                      </button>
                      <button
                        type="button"
                        title={t('lotSetup.removePhoto')}
                        aria-label={t('lotSetup.removePhoto')}
                        onClick={() => removePending(photo)}
                        className="grid h-9 w-9 place-items-center rounded-md bg-white text-danger shadow-sm"
                      >
                        <Trash2 size={17} />
                      </button>
                    </div>
                  )}
                </div>
                {photo.message ? (
                  <span className="absolute inset-x-1.5 bottom-1.5 rounded bg-white/95 px-2 py-1 text-[10px] text-danger">
                    {photo.message}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="grid min-h-28 place-items-center rounded-lg border border-dashed border-hairline bg-surface-muted">
            <ImagePlus size={24} className="text-faint" aria-hidden />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <label
            htmlFor={inputId}
            className={`inline-flex min-h-10 items-center gap-2 rounded-lg border-[1.5px] border-hairline px-3 text-[13px] font-semibold ${
              disabled || photos.length + pending.length >= MAX_PHOTOS
                ? 'cursor-not-allowed opacity-40'
                : 'cursor-pointer hover:bg-surface-muted'
            }`}
          >
            <ImagePlus size={17} />
            {t('lotSetup.uploadPhotos')}
          </label>
          <span className="text-[11px] text-faint">{t('lotSetup.photoFormats')}</span>
        </div>
        <input
          id={inputId}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          disabled={disabled || photos.length + pending.length >= MAX_PHOTOS}
          className="sr-only"
          onChange={(event) => {
            selectFiles(event.target.files);
            event.target.value = '';
          }}
        />
        {selectionError ? (
          <span className="flex items-center gap-1.5 text-[11px] text-danger">
            <AlertCircle size={14} />
            {selectionError}
          </span>
        ) : null}
      </div>
    </Field>
  );
}

function EditField({
  label,
  value,
  onChange,
  disabled,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  type?: string;
}) {
  return (
    <Field label={label}>
      <Input
        type={type}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}

function TextArea({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <Field label={label}>
      <textarea
        value={value}
        disabled={disabled}
        rows={3}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-lg border border-hairline px-3 py-2 text-[13px] outline-none focus:border-ink"
      />
    </Field>
  );
}

function StatusBadge({ status }: { status: Lot['status'] }) {
  const { t } = useTranslation();
  return (
    <Badge tone={status === 'online' ? 'success' : status === 'offline' ? 'warning' : 'neutral'}>
      {t(`lotSetup.${status}`)}
    </Badge>
  );
}

function QrCard({ lot }: { lot: Lot }) {
  const { t } = useTranslation();
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    QRCode.toDataURL(lot.qr.url, { width: 320, margin: 1, errorCorrectionLevel: 'M' })
      .then((url) => {
        if (active) setDataUrl(url);
      })
      // Without this a rejection leaves the card on its skeleton forever, with
      // nothing in the console to say why.
      .catch((cause) => console.error('QR generation failed', cause));
    return () => {
      active = false;
    };
  }, [lot.qr.url]);

  const download = () => {
    if (!dataUrl) return;
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `parkingpay-${lot.shortCode}-qr.png`;
    link.click();
  };

  const print = () => {
    if (!dataUrl) return;
    const popup = window.open('', '_blank', 'width=560,height=720');
    if (!popup) return;
    popup.document.title = lot.name;
    popup.document.body.style.cssText = 'font-family:Arial;text-align:center;padding:48px';
    const heading = popup.document.createElement('h1');
    heading.textContent = lot.name;
    const image = popup.document.createElement('img');
    image.width = 360;
    image.height = 360;
    image.src = dataUrl;
    const code = popup.document.createElement('h2');
    code.style.letterSpacing = '8px';
    code.textContent = lot.shortCode;
    const url = popup.document.createElement('p');
    url.textContent = lot.qr.url;
    let printed = false;
    const printWhenReady = () => {
      if (printed) return;
      printed = true;
      popup.focus();
      popup.print();
    };
    image.addEventListener('load', printWhenReady, { once: true });
    popup.document.body.append(heading, image, code, url);
    if (image.complete) window.setTimeout(printWhenReady, 0);
  };

  return (
    <Card className="flex h-fit flex-col items-center gap-3 text-center">
      <h3 className="text-[13px] font-bold">{t('lotSetup.qrTitle')}</h3>
      {dataUrl ? (
        <img alt={t('lotSetup.qrAlt', { lot: lot.name })} width={160} height={160} src={dataUrl} />
      ) : (
        <div className="h-40 w-40 animate-pulse bg-surface-muted" />
      )}
      <p className="num text-xl font-bold tracking-[2px]">{lot.shortCode}</p>
      <div className="grid w-full grid-cols-2 gap-2">
        <Button onClick={download}>{t('lotSetup.downloadQr')}</Button>
        <Button onClick={print}>{t('lotSetup.printQr')}</Button>
      </div>
      <a href={lot.qr.url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-accent">
        {t('lotSetup.preview')}
      </a>
    </Card>
  );
}
